const toClipGid = (id: string) => btoa(`Clip/${id}`);

export interface IBulkClipItem {
  id: string;
  internalId: string;
  isSaved: boolean;
}

export interface SaveResult {
  success: boolean;
  count: number;
  savedCount: number;
  limit: number | null;
  savedIds: string[];
}

export type SaveClipFn = (options: {
  variables: { input: { clipIds: string[] } };
}) => Promise<{
  data?: {
    clipSave?: {
      success: boolean;
      savedCount: number;
      limit: number | null;
      errors?: { code?: string; message: string }[];
    };
  };
}>;

export async function saveSelectedClips(
  selectedIds: string[],
  clips: IBulkClipItem[],
  saveClipMutation: SaveClipFn,
): Promise<SaveResult> {
  const clipInternalIds = selectedIds
    .map((id) => clips.find((c) => c.id === id))
    .filter(Boolean)
    .map((c) => c!.internalId);

  if (clipInternalIds.length === 0) {
    return { success: false, count: 0, savedCount: 0, limit: 0, savedIds: [] };
  }

  const clipGids = clipInternalIds.map(toClipGid);

  const result = await saveClipMutation({
    variables: { input: { clipIds: clipGids } },
  });

  if (!result.data?.clipSave?.success) {
    return {
      success: false,
      count: 0,
      savedCount: result.data?.clipSave?.savedCount ?? 0,
      limit: result.data?.clipSave?.limit ?? null,
      savedIds: [],
    };
  }

  return {
    success: true,
    count: clipInternalIds.length,
    savedCount: result.data.clipSave.savedCount,
    limit: result.data.clipSave.limit,
    savedIds: clipInternalIds,
  };
}

export interface UnsaveResult {
  success: boolean;
  count: number;
  savedCount: number;
  limit: number | null;
  unsavedIds: string[];
}

export type UnsaveClipFn = (options: {
  variables: { input: { clipIds: string[] } };
}) => Promise<{
  data?: {
    clipUnsave?: {
      success: boolean;
      savedCount: number;
      limit: number | null;
      errors?: { message: string }[];
    };
  };
}>;

export async function unsaveSelectedClips(
  selectedIds: string[],
  clips: IBulkClipItem[],
  unsaveClipMutation: UnsaveClipFn,
): Promise<UnsaveResult> {
  const clipInternalIds = selectedIds
    .map((id) => clips.find((c) => c.id === id))
    .filter(Boolean)
    .map((c) => c!.internalId);

  if (clipInternalIds.length === 0) {
    return {
      success: false,
      count: 0,
      savedCount: 0,
      limit: 0,
      unsavedIds: [],
    };
  }

  const clipGids = clipInternalIds.map(toClipGid);

  const result = await unsaveClipMutation({
    variables: { input: { clipIds: clipGids } },
  });

  if (!result.data?.clipUnsave?.success) {
    return {
      success: false,
      count: 0,
      savedCount: 0,
      limit: 0,
      unsavedIds: [],
    };
  }

  return {
    success: true,
    count: clipInternalIds.length,
    savedCount: result.data.clipUnsave.savedCount,
    limit: result.data.clipUnsave.limit,
    unsavedIds: clipInternalIds,
  };
}
